import React from "react";
import type { ComponentStatus } from "../types";

interface UploadInfo {
  fileName: string;
  status: "uploading" | "processing" | "done" | "error" | "rejected";
  pinCount?: number;
  errors?: string[];
}

interface Props {
  components: ComponentStatus[];
  projectId: string;
  onFileSelected: (projectId: string, partNumber: string, file: File) => void;
  onDownloadComponentJson: (projectId: string, partNumber: string) => void;
  onGenerateAll: (projectId: string) => void;
  onForceUploadToggle: (partNumber: string, forced: boolean) => void;
  uploads: Record<string, UploadInfo>;
  forcedParts: Set<string>;
}

const ComponentStatusTable: React.FC<Props> = ({
  components, projectId, onFileSelected, onDownloadComponentJson,
  onGenerateAll, onForceUploadToggle, uploads, forcedParts,
}) => {
  if (!components || components.length === 0) return null;

  const ics = components.filter((c) => c.refdes.startsWith("U") && c.part_number);

  return (
    <div className="comp-status-table">
      <div className="cst-header">
        <h4>Component Status</h4>
        <div className="cst-flow-row">
          <p className="cst-flow-hint">
            Flow: Upload datasheet &rarr; Generate circuit.json &rarr; Review &amp; modify &rarr; Upload revised JSON
            {" "}<a href="/samples/ML63Q2557.circuit.json" download className="pd-sample-link">&darr; Sample JSON</a>
          </p>
          <button className="cst-btn cst-btn-gen-all" onClick={() => onGenerateAll(projectId)} title="Generate circuit.json for all ICs from MCP server">
            ⬇ Generate All
          </button>
        </div>
        <table className="cst-legend">
          <thead>
            <tr><th>Status</th><th>Meaning</th><th>Action</th></tr>
          </thead>
          <tbody>
            <tr><td>&#x1F4CC; Pin map</td><td>Pin names exist but no verified circuit.json</td><td>Click &#x2B07; .json &rarr; review &rarr; click &#x2B06; .json</td></tr>
            <tr><td>&#x2705; Ready</td><td>MCP or local has full circuit block</td><td>Nothing required</td></tr>
            <tr><td>&#x2B1C; Needs data</td><td>Nothing exists anywhere</td><td>Upload &#x1F4CE; PDF first, then &#x2B07; .json, then &#x2B06; .json</td></tr>
          </tbody>
        </table>
      </div>

      <table className="cst-table">
        <thead>
          <tr>
            <th>Ref</th>
            <th>Part</th>
            <th>Status</th>
            <th>1. Datasheet</th>
            <th>2. Get JSON</th>
            <th>3. Upload JSON</th>
            <th>Force</th>
          </tr>
        </thead>
        <tbody>
          {ics.map((c) => {
            const pdfUp = uploads[c.part_number];
            const jsonUp = uploads[c.part_number + "_json"];
            const hasPdf = pdfUp && pdfUp.status === "done";
            const hasJson = jsonUp && jsonUp.status === "done";
            const isForced = forcedParts.has(c.part_number);

            return (
              <tr key={c.refdes} className="cst-row">
                <td className="cst-ref">{c.refdes}</td>
                <td className="cst-part">{c.part_number}</td>
                <td>
                  {hasJson ? (
                    <span className="cst-badge-ok">&#x2705; JSON</span>
                  ) : hasPdf ? (
                    <span className="cst-badge-pdf">&#x1F4C4; PDF</span>
                  ) : c.status === "resolved" ? (
                    <span className="cst-badge-ok">&#x2705; Ready</span>
                  ) : c.status === "pin_map_only" ? (
                    <span className="cst-badge-pinmap">&#x1F4CC; Pin map</span>
                  ) : (
                    <span className="cst-badge-miss">&#x2B1C; Needs data</span>
                  )}
                </td>
                {/* 1. Datasheet upload */}
                <td>
                  <label className="cst-btn cst-btn-pdf">
                    {pdfUp ? (
                      pdfUp.status === "done" ? `\u2713 ${pdfUp.pinCount}p` :
                      pdfUp.status === "error" ? "\u2717 retry" : "\u27F3"
                    ) : "\u{1F4CE} PDF"}
                    <input
                      type="file"
                      accept=".pdf"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onFileSelected(projectId, c.part_number, file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </td>
                {/* 2. Download generated circuit.json */}
                <td>
                  <button
                    className="cst-btn cst-btn-dl"
                    onClick={() => onDownloadComponentJson(projectId, c.part_number)}
                    title="Generate & download circuit.json from datasheet/built-in/MCP data"
                  >
                    &#x2B07; .json
                  </button>
                  {!hasJson && hasPdf && (
                    <span className="cst-draft-hint" title="Draft will be AI-generated. Review before uploading.">&#x26A0;</span>
                  )}
                </td>
                {/* 3. Upload revised circuit.json */}
                <td>
                  <label className={`cst-btn cst-btn-json ${jsonUp?.status === "done" ? "cst-btn-done" : (jsonUp?.status === "error" || jsonUp?.status === "rejected") ? "cst-btn-err" : ""}`}>
                    {jsonUp ? (
                      jsonUp.status === "done" ? "\u2705 Re-upload" :
                      jsonUp.status === "rejected" ? "\u274C Fix & Re-upload" :
                      jsonUp.status === "error" ? "\u274C Retry" : "\u27F3"
                    ) : "\u{2B06} .json"}
                    <input
                      type="file"
                      accept=".json"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onFileSelected(projectId, c.part_number, file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {jsonUp?.status === "rejected" && jsonUp.errors && (
                    <div className="cst-reject-errors">
                      {jsonUp.errors.map((err, i) => (
                        <div key={i} className="cst-reject-err">❌ {err}</div>
                      ))}
                    </div>
                  )}
                </td>
                {/* Force use uploaded JSON checkbox */}
                <td className="cst-force-cell">
                  <input
                    type="checkbox"
                    checked={isForced}
                    onChange={(e) => onForceUploadToggle(c.part_number, e.target.checked)}
                    title="Force use uploaded JSON (skip MCP server)"
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ComponentStatusTable;
